import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const STORAGE_KEY = 'zhelta_currency';
const BACKEND_URL = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_URL}/api`;

// Common currency set. `symbol` displays in UI; `rate` is the static fallback
// multiplier from USD (base). Live rates override these via /api/fx/rates.
export const CURRENCIES = [
  { code: 'USD', symbol: '$',    name: 'US Dollar',       rate: 1 },
  { code: 'EUR', symbol: '\u20AC', name: 'Euro',            rate: 0.92 },
  { code: 'GBP', symbol: '\u00A3', name: 'British Pound',   rate: 0.78 },
  { code: 'JPY', symbol: '\u00A5', name: 'Japanese Yen',    rate: 156 },
  { code: 'CNY', symbol: '\u00A5', name: 'Chinese Yuan',    rate: 7.25 },
  { code: 'INR', symbol: '\u20B9', name: 'Indian Rupee',    rate: 83 },
  { code: 'AED', symbol: 'د.إ',    name: 'UAE Dirham',      rate: 3.67 },
  { code: 'BRL', symbol: 'R$',     name: 'Brazilian Real',  rate: 5.1 },
  { code: 'CAD', symbol: 'C$',     name: 'Canadian Dollar', rate: 1.37 },
  { code: 'AUD', symbol: 'A$',     name: 'Australian Dollar', rate: 1.5 },
  { code: 'GHS', symbol: 'GH₵',    name: 'Ghanaian Cedi',   rate: 15.5 },
];

const CurrencyContext = createContext({
  currency: 'USD',
  setCurrency: () => {},
  format: (n) => `$${n}`,
  info: CURRENCIES[0],
  rates: {},
  ratesMeta: null,
});

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState('USD');
  const [liveRates, setLiveRates] = useState(null); // { USD: 1, EUR: 0.92, ... }
  const [ratesMeta, setRatesMeta] = useState(null); // { provider, fetched_at }

  useEffect(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (stored && CURRENCIES.some((c) => c.code === stored)) {
      setCurrencyState(stored);
    }
  }, []);

  // Fetch live FX rates once on mount — backend caches for 24h.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/fx/rates`);
        if (cancelled) return;
        if (res.data && res.data.rates) {
          setLiveRates(res.data.rates);
          setRatesMeta({
            provider: res.data.provider,
            fetchedAt: res.data.fetched_at,
            updatedAt: res.data.time_last_update_utc || res.data.fetched_at,
          });
        }
      } catch (_e) {
        /* keep static fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setCurrency = useCallback((code) => {
    setCurrencyState(code);
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch (_e) {
      /* ignore quota / private mode */
    }
  }, []);

  const baseInfo = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];
  const effectiveRate = liveRates && liveRates[currency] ? liveRates[currency] : baseInfo.rate;
  const info = useMemo(
    () => ({ ...baseInfo, rate: effectiveRate }),
    [baseInfo, effectiveRate],
  );

  const format = useCallback(
    (usdAmount) => {
      const converted = usdAmount * info.rate;
      const rounded = info.code === 'JPY' || info.code === 'CNY'
        ? Math.round(converted)
        : Math.round(converted * 100) / 100;
      return `${info.symbol}${rounded.toLocaleString()}`;
    },
    [info]
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, format, info, rates: liveRates || {}, ratesMeta }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

// Append `?currency=CODE` to external links (e.g., the Zhelta Services shop)
export function appendCurrency(url, code) {
  if (!url || !code) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('currency', code);
    return u.toString();
  } catch (_e) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}currency=${code}`;
  }
}
